import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { Alert, AlertType } from '../Alert/Alert';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { CheckboxDecorator } from '../CheckboxDecorator/CheckboxDecorator';
import {
    GridCellContentOverflow,
    PaddingGridCellContentOverflow
} from '../GridCellContentOverflow/GridCellContentOverflow';
import { HoverTooltipWrapper } from '../HoverTooltipWrapper/HoverTooltipWrapper';
import { Loader } from '../Loader/Loader';
import { Shape2, Shape2Icon, Shape2Tone } from '../Shape2/Shape2';
import {
    TableData,
    TableDataTheme,
    TableSizeType
} from '../TableData/TableData';

import './PhraseAdjustment.css';
import * as keyset from './PhraseAdjustment.i18n';

const i18n = i18nFactory(keyset);

export interface PhraseAdjustmentProps {
    cls?: string;
    showsForecast?: number;
    items?: PhraseAdjustmentItem[];
    hasMoreItems: boolean;
    onChange: (items: PhraseAdjustmentItem[]) => void;
    onComponentDidMount: () => void;
    onComponentWillUnmount: () => void;
    onLoadMoreClick: () => void;
}

export interface PhraseAdjustmentItem {
    checked: boolean;
    minusWord: string;
    shows: number;
    requestExample: string;
}

export class PhraseAdjustment extends React.Component<PhraseAdjustmentProps> {
    componentDidMount() {
        this.props.onComponentDidMount();
    }

    componentWillUnmount() {
        this.props.onComponentWillUnmount();
    }

    render() {
        const { showsForecast, cls } = this.props;

        return (
            <div className={classNames(cls, Bem.cls('phrase-adjustment'))}>
                <div className={Bem.cls('phrase-adjustment', 'info')}>
                    <div className={Bem.cls('phrase-adjustment', 'forecast')}>
                        <HoverTooltipWrapper
                            to={'top'}
                            isDoccenterTooltip={false}
                            theme={'normal'}
                            tooltipText={
                                <div>
                                    <div
                                        className={Bem.cls(
                                            'phrase-adjustment',
                                            'info-text'
                                        )}
                                    >
                                        {i18n('phrase-adjustment-info-line-1')}
                                    </div>
                                    <div
                                        className={Bem.cls(
                                            'phrase-adjustment',
                                            'info-text'
                                        )}
                                    >
                                        {i18n('phrase-adjustment-info-line-2')}
                                    </div>
                                </div>
                            }
                        >
                            {i18n('shows-per-mounth', { shows: showsForecast })}
                            <Shape2
                                cls={Bem.cls(
                                    'phrase-adjustment',
                                    'question-icon'
                                )}
                                width={14}
                                height={14}
                                icon={Shape2Icon.question}
                                tone={Shape2Tone.dark}
                            />
                        </HoverTooltipWrapper>
                    </div>
                </div>
                {this.getTable()}
            </div>
        );
    }

    private getTable() {
        const { items, hasMoreItems } = this.props;

        if (!items) {
            return <Loader size={'s'} />;
        }

        if (!items.length) {
            return (
                <Alert
                    className={Bem.cls('phrase-adjustment', 'warning')}
                    type={AlertType.warning}
                    iconTone={Shape2Tone.yellow}
                >
                    {i18n('cant-adjust-phrase')}
                </Alert>
            );
        }

        const { minusWordCells, exampleCells } = items.reduce(
            (
                acc: {
                    minusWordCells: Array<JSX.Element | string>;
                    exampleCells: Array<JSX.Element | string>;
                },
                el
            ) => {
                acc.minusWordCells.push(
                    <GridCellContentOverflow
                        sizeHiding={PaddingGridCellContentOverflow.M}
                    >
                        <div className={Bem.cls('phrase-adjustment', 'cell')}>
                            <div title={el.minusWord}>
                                <CheckboxDecorator
                                    theme="normal"
                                    size="m"
                                    item={el}
                                    onChecked={this.toggleCheckbox}
                                    checked={el.checked}
                                >
                                    {el.minusWord}
                                </CheckboxDecorator>
                            </div>
                            <div
                                className={Bem.cls(
                                    'phrase-adjustment',
                                    'cell-shows-text'
                                )}
                                title={String(el.shows)}
                            >
                                {el.shows} {i18n('count-views-per-month')}
                            </div>
                        </div>
                    </GridCellContentOverflow>
                );

                acc.exampleCells.push(
                    <div className={Bem.cls('phrase-adjustment', 'cell')}>
                        <div
                            className={Bem.cls(
                                'phrase-adjustment',
                                'request-example'
                            )}
                        >
                            {el.requestExample}
                        </div>
                    </div>
                );

                return acc;
            },
            {
                minusWordCells: [],
                exampleCells: []
            }
        );

        if (hasMoreItems) {
            minusWordCells.push(
                <ButtonDecorator
                    size={'n'}
                    theme={'action'}
                    onClick={this.onLoadMoreClick}
                    text={i18n('load-more')}
                />
            );
        }

        const columns = [
            {
                width: 250,
                header: (
                    <CheckboxDecorator
                        theme="normal"
                        size="m"
                        onChecked={this.toggleAllCheckbox}
                        checked={items.every(el => el.checked)}
                    >
                        <span
                            className={Bem.cls(
                                'phrase-adjustment',
                                'minus-word-header'
                            )}
                        >
                            {i18n('minus-word-header')}
                        </span>
                    </CheckboxDecorator>
                ),
                cells: minusWordCells
            },
            {
                width: 450,
                header: i18n('minus-word-request-example-header'),
                cells: exampleCells
            }
        ];

        return (
            <div className={Bem.cls('phrase-adjustment', 'table-container')}>
                <TableData
                    theme={TableDataTheme.light}
                    columns={columns}
                    size={TableSizeType.small}
                    rowHeight={80}
                    overflow={true}
                />
            </div>
        );
    }

    private toggleCheckbox = (
        e: React.KeyboardEvent<HTMLElement>,
        data: { checked: boolean; item: PhraseAdjustmentItem }
    ) => {
        const { onChange, items } = this.props;

        if (items) {
            onChange(
                items.map(el => ({
                    ...el,
                    checked:
                        data.item.minusWord === el.minusWord
                            ? !el.checked
                            : el.checked
                }))
            );
        }
    };

    private toggleAllCheckbox = (
        e: React.KeyboardEvent<HTMLElement>,
        data: { checked: boolean }
    ) => {
        const { onChange, items } = this.props;

        if (items) {
            onChange(
                items.map(el => ({
                    ...el,
                    checked: data.checked
                }))
            );
        }
    };

    private onLoadMoreClick = () => {
        this.props.onLoadMoreClick();
    };
}
